import { reactive, ref } from 'vue'

export interface TipOptions {
  title: string
  content: string
  confirmText?: string
  showCancel?: boolean
  onConfirm?: () => void
}

/** 策略保存等接口额外字段：无权限解锁跳转资讯页 */
export interface BacktestStatusPayload {
  articleID?: string | number
}

function isStarTestEnv(): boolean {
  const hostname = location.hostname
  return (
    hostname.includes('test') || hostname.includes('localhost')
  )
}

/** 星投解锁二维码：测试 / 生产短链 */
export function getStarUnlockQrUrl(): string {
  return isStarTestEnv()
    ? 'https://test-s.jinyi999.cn/xpUZs7'
    : 'https://s.techgp.cn/f02Y8u'
}

/** 服务协议开通二维码：测试 / 生产短链 */
export function getStarServiceQrUrl(): string {
  return isStarTestEnv()
    ? 'https://test-s.jinyi999.cn/vh326n'
    : 'https://s.techgp.cn/f02Y8u'
}

function buildUnlockArticleUrl(newsId: string | number): string {
  const isTest = isStarTestEnv()
  const base = isTest
    ? 'https://test-h5.techgp.cn/zxg-pc/index.html#/article-detail-v2'
    : 'https://h5.chongnengjihua.com/zxg-pc/index.html#/article-detail-v2'
  return `${base}?newsId=${encodeURIComponent(String(newsId))}&appCode=com.rjhy.uranus`
}

function openUnlockArticleInWindow(newsId: string | number) {
  const url = `gw://webkitdlg?width=948;height=575;mode=0;title=星投解锁;allowdestroy=1;url=${buildUnlockArticleUrl(newsId)}`
  window.open(url, '_blank', 'noopener,noreferrer')
}

export function useStarTip() {
  const tipDialog = reactive({
    visible: false,
    title: '',
    content: '',
    confirmText: '确认',
    showCancel: false,
    onConfirm: () => { tipDialog.visible = false }
  })

  const showQrDialog = ref(false)

  const showTip = (opts: TipOptions) => {
    tipDialog.title = opts.title
    tipDialog.content = opts.content
    tipDialog.confirmText = opts.confirmText || '确认'
    tipDialog.showCancel = opts.showCancel || false
    tipDialog.onConfirm = opts.onConfirm || (() => { tipDialog.visible = false })
    tipDialog.visible = true
  }

  const showUnlockQr = async () => {
    tipDialog.visible = false
    showQrDialog.value = true
  }

  /**
   * 处理订阅/保存（strategyAction）等非一键回测接口返回的 status
   * status 1: 成功 → onSuccess
   * status 2: 名称已存在
   * status 3 / 4: 文案用接口 toast；「立即解锁」打开资讯详情（articleID 作 newsId）
   * status 5: 文案用 toast；「立即刷新」整页刷新
   */
  const handleBacktestStatus = (
    status: number,
    toast: string | undefined,
    onSuccess: () => void,
    payload?: BacktestStatusPayload
  ) => {
    if (status === 1) {
      onSuccess()
      return
    }
    const aid = payload?.articleID ?? ''
    const content = toast || ''
    if (status === 2) {
      showTip({ title: '温馨提示', content: '名称已存在', confirmText: '确认', showCancel: false, onConfirm: () => { tipDialog.visible = false } })
      return
    }
    if (status === 3 || status === 4) {
      showTip({
        title: '温馨提示',
        content,
        confirmText: '立即解锁',
        showCancel: true,
        onConfirm: () => {
          tipDialog.visible = false
          openUnlockArticleInWindow(aid)
        }
      })
      return
    }
    if (status === 5) {
      showTip({
        title: '温馨提示',
        content: toast || '操作失败，请稍后重试',
        confirmText: '立即刷新',
        showCancel: false,
        onConfirm: () => {
          tipDialog.visible = false
          window.location.reload()
        }
      })
      return
    }
    showTip({ title: '温馨提示', content: toast || '操作失败，请稍后重试', confirmText: '确认', showCancel: false, onConfirm: () => { tipDialog.visible = false } })
  } 

  /**
   * 一键回测（doTestCom 等）接口 status
   * 1: 成功 → onSuccess
   * status 2 / 3: 文案用 toast；「立即解锁」打开资讯详情（articleID）
   * status 4 / 5: toast +「立即刷新」
   */
  const handleOneClickBacktestStatus = (
    status: number | undefined,
    toast: string | undefined,
    onSuccess: () => void,
    payload?: BacktestStatusPayload
  ) => {
    if (status === 1) {
      onSuccess()
      return
    }
    const aid = payload?.articleID ?? ''
    const content = toast || ''
    if (status === 2 || status === 3) {
      showTip({
        title: '温馨提示',
        content,
        confirmText: '立即解锁',
        showCancel: true,
        onConfirm: () => {
          tipDialog.visible = false
          openUnlockArticleInWindow(aid)
        }
      })
      return
    }
    if (status === 4 || status === 5) {
      showTip({
        title: '温馨提示',
        content: toast || '操作失败，请稍后重试',
        confirmText: '立即刷新',
        showCancel: false,
        onConfirm: () => {
          tipDialog.visible = false
          window.location.reload()
        }
      })
      return
    }
    showTip({ title: '温馨提示', content: toast || '回测创建失败，请稍后重试', confirmText: '确认', showCancel: false, onConfirm: () => { tipDialog.visible = false } })
  }

  return { tipDialog, showQrDialog, showTip, showUnlockQr, handleBacktestStatus, handleOneClickBacktestStatus }
}
